/**
 * Network Connectivity Service
 *
 * Layered offline/online detection:
 *  1. @capacitor/network — native OS events on iOS/Android (most reliable on mobile)
 *  2. Browser online/offline events — instant signal on web
 *  3. GET /api/v1/health — periodic poll against own API (source of truth)
 *  4. navigator.onLine — synchronous fast-path negative signal
 *
 * Adaptive polling: 3 s when offline (fast recovery), 30 s when online (battery-friendly).
 *
 * Pause/resume: Long-lived connections (e.g. SSE streams) that hold a server worker
 * should call pause() before starting and resume() when done. While paused, health
 * checks are skipped — the active connection itself proves connectivity.
 */

import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { fireAndForget } from '../utils/fireAndForget';

type NetworkListener = (isOnline: boolean) => void;

interface NetworkState {
  isOnline: boolean;
  lastCheck: number;
}

const ONLINE_INTERVAL = 30_000; // 30 s — just a keep-alive while things are fine
const OFFLINE_INTERVAL = 3_000; // 3 s — aggressive retry so recovery feels instant
const CHECK_COOLDOWN = 2_000; // min gap between consecutive health checks
const CHECK_TIMEOUT = 5_000; // abort health fetch after 5 s
const OFFLINE_THRESHOLD = 2; // consecutive health check failures before going offline

const HEALTH_URL = `${import.meta.env.VITE_API_URL}/health`;

class NetworkService {
  private isOnline: boolean;
  private listeners: NetworkListener[];
  private checkInterval: ReturnType<typeof setInterval> | null;
  private lastCheckTime: number;
  private isInitialized?: boolean;
  private pauseCount: number;

  // Pre-bound browser event handlers (for clean add/remove)
  private onBrowserOnline: () => void;
  private onBrowserOffline: () => void;
  private onVisibilityChange: () => void;
  private onWindowFocus: () => void;

  private consecutiveFailures: number;

  constructor() {
    this.isOnline = true;
    this.listeners = [];
    this.checkInterval = null;
    this.lastCheckTime = 0;
    this.pauseCount = 0;
    this.consecutiveFailures = 0;

    this.onBrowserOnline = () => this.onSignalChange(true);
    this.onBrowserOffline = () => this.onSignalChange(false);
    this.onVisibilityChange = () => {
      if (!document.hidden) this.checkConnectivity().catch(() => {});
    };
    this.onWindowFocus = fireAndForget(() => this.checkConnectivity().catch(() => {}));
  }

  // ── Lifecycle ──────────────────────────────────────────────

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    this.isOnline = await this.checkInitialState();
    this.setupEventListeners();
    this.startPeriodicChecks();
    this.isInitialized = true;

    // Async verify against API and emit if different from initial guess
    this.performHealthCheck()
      .then((online) => this.handleOnlineChange(online))
      .catch(() => {});

    console.log('Network service initialized, initial state:', this.isOnline);
  }

  destroy(): void {
    this.stopPeriodicChecks();
    this.listeners = [];
    this.isInitialized = false;

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onBrowserOnline);
      window.removeEventListener('offline', this.onBrowserOffline);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('focus', this.onWindowFocus);
    }

    if (Capacitor.isNativePlatform()) {
      Network.removeAllListeners().catch(() => {});
    }
  }

  // ── Initial state ──────────────────────────────────────────

  private async checkInitialState(): Promise<boolean> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false;
    }
    if (Capacitor.isNativePlatform()) {
      try {
        const status = await Network.getStatus();
        if (!status.connected) return false;
      } catch {
        /* fall through to health check */
      }
    }
    return this.performHealthCheck();
  }

  // ── Event listeners ────────────────────────────────────────

  private setupEventListeners(): void {
    if (typeof window === 'undefined') return;

    // Browser events (web + webview)
    window.addEventListener('online', this.onBrowserOnline);
    window.addEventListener('offline', this.onBrowserOffline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onWindowFocus);

    // Capacitor native events (iOS/Android)
    if (Capacitor.isNativePlatform()) {
      void Network.addListener('networkStatusChange', (status) => {
        this.onSignalChange(status.connected);
      });
    }
  }

  /**
   * Called when any instant signal (browser event or Capacitor event) fires.
   * Triggers an immediate real health check to confirm.
   */
  private onSignalChange(hintOnline: boolean): void {
    if (!hintOnline) {
      // Offline signals are trustworthy — apply immediately
      this.handleOnlineChange(false);
    }
    // Always confirm with a real health check (skips cooldown)
    this.lastCheckTime = 0;
    this.checkConnectivity().catch(() => {});
  }

  // ── Periodic polling ───────────────────────────────────────

  private startPeriodicChecks(): void {
    this.stopPeriodicChecks();
    const interval = this.isOnline ? ONLINE_INTERVAL : OFFLINE_INTERVAL;
    this.checkInterval = setInterval(() => {
      this.checkConnectivity().catch(() => {});
    }, interval);
  }

  private stopPeriodicChecks(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /** Restart the timer with the right frequency for the current state. */
  private restartPeriodicChecks(): void {
    if (this.isInitialized) this.startPeriodicChecks();
  }

  // ── Connectivity check ─────────────────────────────────────

  async checkConnectivity(): Promise<void> {
    // Skip health checks while paused (an active connection proves connectivity)
    if (this.pauseCount > 0) return;

    const now = Date.now();
    if (now - this.lastCheckTime < CHECK_COOLDOWN) return;
    this.lastCheckTime = now;

    // Fast-path: navigator.onLine === false ⇒ definitely offline
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.handleOnlineChange(false);
      return;
    }

    const online = await this.performHealthCheck();

    if (online) {
      this.consecutiveFailures = 0;
      this.handleOnlineChange(true);
    } else {
      this.consecutiveFailures++;
      // Only go offline after consecutive failures — a single timeout
      // (e.g. dev server busy with long request) shouldn't trigger offline
      if (this.consecutiveFailures >= OFFLINE_THRESHOLD) {
        this.handleOnlineChange(false);
      }
    }
  }

  /** Hit our own API health endpoint. Returns true if reachable. */
  private async performHealthCheck(): Promise<boolean> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT);

      const response = await fetch(HEALTH_URL, {
        method: 'GET',
        cache: 'no-cache',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      if (timeoutId) clearTimeout(timeoutId);
      return false;
    }
  }

  // ── State change ───────────────────────────────────────────

  private handleOnlineChange(isOnline: boolean): void {
    if (this.isOnline === isOnline) return;

    console.log(`Network state changed: ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
    this.isOnline = isOnline;

    // Switch polling frequency
    this.restartPeriodicChecks();

    // Notify listeners
    for (const listener of this.listeners) {
      try {
        listener(isOnline);
      } catch (error) {
        console.error('Error in network state listener:', error);
      }
    }
  }

  // ── Public API ─────────────────────────────────────────────

  addListener(listener: NetworkListener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  getNetworkState(): NetworkState {
    return { isOnline: this.isOnline, lastCheck: this.lastCheckTime };
  }

  /**
   * Pause health checks while a long-lived connection (e.g. SSE stream) is active.
   * Uses a counter so nested/concurrent streams are handled correctly.
   */
  pause(): void {
    this.pauseCount++;
  }

  /**
   * Resume health checks after a long-lived connection ends.
   */
  resume(): void {
    this.pauseCount = Math.max(0, this.pauseCount - 1);
  }

  /** Force an immediate health check, bypassing cooldown. */
  async forceCheck(): Promise<boolean> {
    this.lastCheckTime = 0;
    const online = await this.performHealthCheck();
    this.handleOnlineChange(online);
    return online;
  }
}

// Singleton
export const networkService = new NetworkService();
export default networkService;
